import { Combobox } from '@base-ui/react/combobox'
import React, { useMemo, useRef, useState } from 'react'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Close } from 'sutra/components/Icons'
import type {
  MultiSelectEntry,
  MultiSelectListGroup,
  MultiSelectOption,
} from 'sutra/components/MultiSelect/multi-select-options'
import {
  appendUnique,
  creatableValue,
  defaultFilter,
  flattenOptions,
  resolveToken,
  splitOnSeparators,
  toListItems,
} from 'sutra/components/MultiSelect/multi-select-options'
import {
  ChipArea,
  ChipHost,
  ClearButton,
  GroupLabel,
  SearchField,
  Shell,
} from 'sutra/components/MultiSelect/multi-select-styles'
import {
  Chevron,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
  NoResults,
  OptionLabel,
} from 'sutra/components/Select/select-styles'
import type { SelectSize } from 'sutra/components/Select/select-styles'

import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

export interface MultiSelectProps<
  O extends MultiSelectOption = MultiSelectOption,
> {
  values: string[]
  onChange: (values: string[]) => void
  // Flat options, headed groups, or a mix (loose options between groups).
  options: Array<MultiSelectEntry<O>>
  placeholder?: string
  disabled?: boolean
  // Shows "Loading…" in the open panel while the list is empty.
  loading?: boolean
  invalid?: boolean
  // Trigger size, using Select's vocabulary; defaults to 'md' (40px).
  size?: SelectSize
  // A clear-all control, shown while anything is selected.
  clearable?: boolean
  // Search predicate; defaults to a case-insensitive substring match on label.
  filter?: (query: string, option: O) => boolean
  // Typed text that matches no option can be added as a chip — with Enter, or
  // by typing one of `tokenSeparators`. Free text becomes its own value.
  creatable?: boolean
  tokenSeparators?: string[]
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  // Overlay z-index for callers embedded in their own stacking context.
  zIndex?: number
  // Which edge of the field the panel's fixed edge aligns to.
  panelAlign?: 'start' | 'end'
}

const DEFAULT_SEPARATORS = [',']

// A multi-value dropdown: the selection is Chiclets inside the field, with the
// search input inline after them. Built on Base UI's Combobox in `multiple`
// mode; the panel is the same one `Select` renders (see select-styles), portaled
// to `document.body`.
export function MultiSelect<O extends MultiSelectOption = MultiSelectOption>({
  values,
  onChange,
  options,
  placeholder,
  disabled = false,
  loading = false,
  invalid = false,
  size = 'md',
  clearable = false,
  filter,
  creatable = false,
  tokenSeparators = DEFAULT_SEPARATORS,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  zIndex = 1000,
  panelAlign = 'start',
}: MultiSelectProps<O>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const shellRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const flatOptions = useMemo(() => flattenOptions(options), [options])
  const optionByValue = useMemo(
    () => new Map(flatOptions.map((option) => [option.value, option])),
    [flatOptions],
  )
  const hasGroups = options.some((entry) => 'options' in entry)
  const createValue = creatable
    ? creatableValue(query, flatOptions, values)
    : null
  const items = useMemo(
    () => toListItems(options, createValue),
    [options, createValue],
  )

  const labelOf = (value: string): string =>
    optionByValue.get(value)?.label ?? value

  // Escape goes through the shared stack so that one press closes only the
  // innermost of several nested overlays (see Select).
  useEscapeDismiss({
    isOpen,
    onClose: () => {
      setIsOpen(false)
      inputRef.current?.focus()
    },
  })

  const handleOpenChange = (open: boolean): void => {
    setQuery('')
    setIsOpen(open)
  }

  const handleInputChange = (next: string): void => {
    const split = creatable ? splitOnSeparators(next, tokenSeparators) : null
    if (!split) {
      setQuery(next)
      return
    }
    const tokens = split.tokens.map((token) => resolveToken(token, flatOptions))
    const merged = appendUnique(values, tokens)
    if (merged.length !== values.length) onChange(merged)
    setQuery(split.rest)
  }

  // A press on the field's own chrome (padding, chevron) behaves like a press
  // on the input; one on a chip, its remove button or the input is left alone.
  const handleShellMouseDown = (event: React.MouseEvent): void => {
    if (disabled) return
    if ((event.target as HTMLElement).closest('button, input')) return
    event.preventDefault()
    inputRef.current?.focus()
    setIsOpen((open) => !open)
  }

  const matches = (item: string, text: string): boolean => {
    if (item === createValue) return true
    const option = optionByValue.get(item)
    if (!option) return false
    return filter ? filter(text, option as O) : defaultFilter(text, option)
  }

  const renderItem = (value: string): React.ReactElement => (
    <ComboboxItem
      key={value}
      value={value}
      disabled={optionByValue.get(value)?.disabled}
    >
      <OptionLabel>
        {value === createValue ? `Create "${value}"` : labelOf(value)}
      </OptionLabel>
    </ComboboxItem>
  )

  const renderGroup = (
    group: MultiSelectListGroup,
    index: number,
  ): React.ReactElement => (
    <Combobox.Group key={index} items={group.items}>
      {group.label !== null && <GroupLabel>{group.label}</GroupLabel>}
      <Combobox.Collection>{renderItem}</Combobox.Collection>
    </Combobox.Group>
  )

  return (
    <Combobox.Root
      multiple
      items={items}
      value={values}
      onValueChange={(next) => onChange(next)}
      itemToStringLabel={labelOf}
      filter={matches}
      inputValue={query}
      onInputValueChange={handleInputChange}
      autoHighlight={creatable}
      open={isOpen}
      onOpenChange={handleOpenChange}
      disabled={disabled}
    >
      <Shell
        ref={shellRef}
        $size={size}
        data-invalid={invalid || undefined}
        data-disabled={disabled || undefined}
        onMouseDown={handleShellMouseDown}
      >
        <ChipArea>
          {values.map((value) => (
            <ChipHost key={value}>
              <Chiclet
                $size={size === 'sm' ? 'sm' : 'md'}
                onRemove={
                  disabled ? undefined : () => onChange(without(values, value))
                }
                removeLabel={`Remove ${labelOf(value)}`}
              >
                {labelOf(value)}
              </Chiclet>
            </ChipHost>
          ))}
          <SearchField
            ref={inputRef}
            id={id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-invalid={invalid || undefined}
            aria-busy={loading || undefined}
            placeholder={values.length === 0 ? placeholder : undefined}
          />
        </ChipArea>
        {clearable && !disabled && (
          <ClearButton aria-label="Clear all">
            <Close width={12} height={12} />
          </ClearButton>
        )}
        <Chevron />
      </Shell>
      <Combobox.Portal>
        <ComboboxPositioner
          anchor={shellRef}
          side="bottom"
          align={panelAlign}
          sideOffset={4}
          $zIndex={zIndex}
        >
          <ComboboxPopup>
            <NoResults>{loading ? 'Loading…' : 'No matches'}</NoResults>
            <ComboboxList>{hasGroups ? renderGroup : renderItem}</ComboboxList>
          </ComboboxPopup>
        </ComboboxPositioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}

const without = (values: string[], removed: string): string[] =>
  values.filter((value) => value !== removed)

MultiSelect.displayName = 'MultiSelect'
